import type { ParticleMaterialProfile } from './profiles';
import {
  ENERGY_DEFAULT,
  energyChipCap,
  energyCountScale,
  energyLaunch,
  mixEnergyCone,
} from '../../services/energy';
import {
  DEFAULT_SURFACE_NORMAL,
  WALL_SPLASH_FRACTION,
  coneAround,
  deriveImpactFrame,
  dot3,
  normalize3,
  reflectIncoming,
} from './impactFrame';
import {
  impactChipScale,
  impactDepositScale,
  impactGritScale,
  impactLingerScale,
  impactSpeedScale,
  impactSplashScale,
  impactSpreadScale,
  impactStampScale,
  mixImpactCone,
  resolveImpact,
  type ImpactParams,
} from './impactRegime';

export const GRAIN_SPAWN_BUDGET = 8192;

export interface GrainSpawnRecord {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly vx: number;
  readonly vy: number;
  readonly vz: number;
  readonly radius: number;
  readonly spin: number;
  readonly restitution: number;
  readonly friction: number;
  readonly materialId: number;
  readonly drag: number;
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly ox: number;
  readonly oy: number;
  readonly oz: number;
  readonly maxLife: number;
  readonly depositScale: number;
}

export interface BurstPlanInput {
  readonly position: readonly [number, number, number];
  readonly direction: readonly [number, number, number];
  readonly energy: number;
  readonly mass: number;
  readonly profile: ParticleMaterialProfile;
  readonly burstIndex: number;
  readonly maxCount?: number;
  readonly surfaceNormal?: readonly [number, number, number];
  readonly impact?: ImpactParams;
}

export interface GrainSpawnCursor {
  next: number;
  capacity: number;
  spawnedThisFrame: number;
}

export function createGrainSpawnCursor(capacity: number): GrainSpawnCursor {
  return { next: 0, capacity: Math.max(0, capacity | 0), spawnedThisFrame: 0 };
}

export function resetGrainSpawnBudget(cursor: GrainSpawnCursor): void {
  cursor.spawnedThisFrame = 0;
}

/** Claim up to `requested` ring slots without exceeding the per-step budget. */
export function takeGrainSpawnSlots(cursor: GrainSpawnCursor, requested: number): number[] {
  if (cursor.capacity <= 0 || requested <= 0) return [];
  const n = Math.min(
    requested | 0,
    GRAIN_SPAWN_BUDGET - cursor.spawnedThisFrame,
    cursor.capacity,
  );
  if (n <= 0) return [];
  const slots = new Array<number>(n);
  for (let i = 0; i < n; i += 1) {
    slots[i] = cursor.next;
    cursor.next = (cursor.next + 1) % cursor.capacity;
  }
  cursor.spawnedThisFrame += n;
  return slots;
}

/**
 * Legacy cursor: does not wrap onto the live prefix. Rewinds only when
 * `liveCount === 0`. Kept for the starvation repro; runtime spawn uses
 * `windowRing.ts`.
 */
export function takeNonStealingSlots(
  cursor: GrainSpawnCursor,
  requested: number,
  liveCount: number,
): number[] {
  if (liveCount <= 0) cursor.next = 0;
  if (cursor.capacity <= 0 || requested <= 0) return [];
  const toEnd = cursor.capacity - cursor.next;
  const room = liveCount <= 0
    ? cursor.capacity
    : Math.max(0, Math.min(toEnd, cursor.capacity - liveCount));
  const n = Math.min(
    requested | 0,
    GRAIN_SPAWN_BUDGET - cursor.spawnedThisFrame,
    room,
  );
  if (n <= 0) return [];
  const slots = new Array<number>(n);
  for (let i = 0; i < n; i += 1) {
    slots[i] = cursor.next;
    cursor.next += 1;
  }
  cursor.spawnedThisFrame += n;
  return slots;
}

/** Split a sequential ring write into row-aligned texSubImage2D ranges. */
export function grainRowSpans(
  start: number,
  count: number,
  texSize: number,
): Array<{ x: number; y: number; n: number; offset: number }> {
  if (count <= 0 || texSize <= 0) return [];
  const spans: Array<{ x: number; y: number; n: number; offset: number }> = [];
  let i = 0;
  let idx = start;
  const area = texSize * texSize;
  while (i < count) {
    const wrapped = ((idx % area) + area) % area;
    const x = wrapped % texSize;
    const y = Math.floor(wrapped / texSize);
    const n = Math.min(texSize - x, count - i);
    spans.push({ x, y, n, offset: i });
    i += n;
    idx += n;
  }
  return spans;
}

export function grainTextureSize(capacity: number): number {
  if (capacity <= 0) return 0;
  return Math.max(1, Math.ceil(Math.sqrt(capacity)));
}

export function grainCapacityForTier(tier: 'desktop' | 'mobile'): number {
  return tier === 'mobile' ? 128 * 128 : 256 * 256;
}

export function heightfieldSizeForTier(tier: 'desktop' | 'mobile'): number {
  return tier === 'mobile' ? 256 : 512;
}

/** Fines state texture: 64² desktop (4,096) / 32² mobile (1,024). */
export function cloudCapacityForTier(tier: 'desktop' | 'mobile'): number {
  return tier === 'mobile' ? 32 * 32 : 64 * 64;
}

/** CPU whole-brick BoxGeometry instances. Mobile is 0 so the overlay stays grit/cloud. */
export function chunkCapacityForTier(tier: 'desktop' | 'mobile'): number {
  return tier === 'mobile' ? 0 : 48;
}

/** CPU chip instances. Follows the chunk budget (desktop 64 / mobile 0). */
export function chipCapacityForTier(tier: 'desktop' | 'mobile'): number {
  return tier === 'mobile' ? 0 : 64;
}

/** Deterministic seed from the burst counter only — no time, no Date. */
export function seedFromBurst(burstIndex: number): number {
  let x = ((burstIndex + 1) * 0x9e3779b9) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x85ebca6b) >>> 0;
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35) >>> 0;
  return (x ^ (x >>> 16)) >>> 0;
}

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) >>> 0;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}



export function plannedBurstCount(
  mass: number,
  profile: ParticleMaterialProfile,
  cap = 1e9,
  energy = ENERGY_DEFAULT,
  impact?: ImpactParams,
): number {
  const raw = Math.round(Math.max(0, mass) * profile.grainsPerKg);
  const gritFrac = Math.min(1, Math.max(0, profile.signature.gritFraction));
  const grit = Math.round(raw * gritFrac * energyCountScale(energy));
  const scaled = Math.round(grit * impactGritScale(resolveImpact(impact)));
  return Math.max(0, Math.min(scaled, Math.max(0, cap)));
}

/** Conceptual grain count before the grit/chip split (mass × grainsPerKg). */
export function plannedGrainCensus(mass: number, profile: ParticleMaterialProfile): number {
  return Math.max(0, Math.round(Math.max(0, mass) * profile.grainsPerKg));
}

export function plannedChipCount(
  mass: number,
  profile: ParticleMaterialProfile,
  cap = 1e9,
  energy = ENERGY_DEFAULT,
  impact?: ImpactParams,
): number {
  const frac = Math.min(1, Math.max(0, profile.signature.chipFraction));
  if (frac <= 0 || profile.signature.chipCap <= 0) return 0;
  const raw = Math.round(plannedGrainCensus(mass, profile) * frac);
  const eCap = energyChipCap(energy, profile.signature.chipCap);
  const scaled = Math.round(Math.min(raw, eCap) * impactChipScale(resolveImpact(impact)));
  return Math.max(0, Math.min(scaled, eCap, Math.max(0, cap)));
}

/**
 * Pure burst planner. Identical (burstIndex, params) → identical records.
 * Seed is a hash of the burst counter — never wall-clock or Math.random.
 */
export function planBurst(input: BurstPlanInput): GrainSpawnRecord[] {
  const { profile } = input;
  const imp = resolveImpact(input.impact);
  const count = plannedBurstCount(input.mass, profile, input.maxCount ?? 1e9, input.energy, input.impact);
  const rng = mulberry32(seedFromBurst(input.burstIndex));
  const origin = input.position;
  const dir = normalize3(input.direction);
  const frame = deriveImpactFrame(input.surfaceNormal ?? DEFAULT_SURFACE_NORMAL);
  const energy = energyLaunch(input.energy);
  const speed0 = 2.4 + energy * 4.2;
  const stamp = impactStampScale(imp);
  const speedMul = impactSpeedScale(imp);
  const lingerMul = impactLingerScale(imp);
  const depositMul = impactDepositScale(imp);
  const splashMul = impactSplashScale(imp);
  const spread0 = 0.22 + energy * 0.08;
  const spread = spread0 * impactSpreadScale(imp);
  const [r0, r1] = profile.grainSizeRange;
  const sizeScale = Math.max(0.02, profile.signature.gritSizeScale);
  const gritLife = profile.signature.gritMaxLife * lingerMul;
  const isGrit = profile.signature.gritMaxLife > 0 && profile.signature.gritMaxLife < 1.5;
  const records: GrainSpawnRecord[] = new Array(count);
  const blendWall = frame.upFactor < 0.999;

  for (let i = 0; i < count; i += 1) {
    const jitter = (0.06 + 0.04 * rng()) * stamp;
    const px = origin[0] + (rng() * 2 - 1) * jitter;
    const py = origin[1] + (rng() * 2 - 1) * jitter * 0.4;
    const pz = origin[2] + (rng() * 2 - 1) * jitter;
    const velDir = coneAround(rng, dir, spread);
    const speed = Math.min(16, speed0 * (0.72 + rng() * 0.56) * speedMul);
    let vx = velDir[0] * speed;
    let vy = velDir[1] * speed;
    let vz = velDir[2] * speed;
    if (blendWall) {
      const graze = 1 - Math.min(1, Math.abs(dot3(dir, frame.normal)));
      const cone = profile.signature.ejectionConeRad;
      const wallSpread0 = cone
        ? mixEnergyCone(cone[0], cone[1], input.energy)
        : spread0 + graze * 0.32;
      const wallSpread = mixImpactCone(wallSpread0, imp);
      const splashFrac = WALL_SPLASH_FRACTION * (1 - frame.upFactor) * splashMul;
      let wDir: [number, number, number];
      if (rng() < splashFrac) {
        const phi = rng() * Math.PI * 2;
        const cp = Math.cos(phi);
        const sp = Math.sin(phi);
        wDir = [
          frame.tangent[0] * cp + frame.bitangent[0] * sp,
          frame.tangent[1] * cp + frame.bitangent[1] * sp,
          frame.tangent[2] * cp + frame.bitangent[2] * sp,
        ];
      } else {
        wDir = coneAround(rng, reflectIncoming(dir, frame.normal), wallSpread);
      }
      const wSpeed = Math.min(16, speed0 * (0.72 + rng() * 0.56) * speedMul);
      const u = frame.upFactor;
      vx = wDir[0] * wSpeed + (vx - wDir[0] * wSpeed) * u;
      vy = wDir[1] * wSpeed + (vy - wDir[1] * wSpeed) * u;
      vz = wDir[2] * wSpeed + (vz - wDir[2] * wSpeed) * u;
    }
    let radius = (r0 + (r1 - r0) * rng()) * sizeScale;
    // Corner-chip enlargement stays on the CPU chip population for brick.
    if (!isGrit && profile.signature.cornerChipChance > 0 && rng() < profile.signature.cornerChipChance) {
      radius *= profile.signature.cornerChipScale;
    }
    const spin = isGrit ? 0 : (rng() * 2 - 1) * 14;
    records[i] = {
      x: px,
      y: py,
      z: pz,
      vx,
      vy,
      vz,
      radius,
      spin,
      restitution: isGrit ? 0 : profile.restitution,
      friction: profile.friction,
      materialId: profile.materialId,
      drag: profile.drag,
      r: profile.grainTint[0],
      g: profile.grainTint[1],
      b: profile.grainTint[2],
      ox: origin[0],
      oy: origin[1],
      oz: origin[2],
      maxLife: gritLife,
      depositScale: profile.signature.grainDepositScale * depositMul,
    };
  }
  return records;
}
